import { EditingCategory, ProductKind, STAGING_PRODUCT_KINDS } from 'constants/product'
import { FC } from 'react'
import { logger } from 'utils/logging'
import { ProductCategory } from '../../../../../models/product'
import { AIEditingDetailsRenderer } from './AIEditingDetailsRenderer.component'
import { AssignmentRendererProps } from './AssignmentRenderer.types'
import { AuthoritiesDocumentsDetailsRenderer } from './AuthoritiesDocumentsDetailsRenderer.component'
import { CertificateOfNeedsDetailsRenderer } from './CertificateOfNeedsDetailsRenderer.component'
import { ConsumptionCertificateDetailsRenderer } from './ConsumptionCertificateDetailsRenderer.component'
import { DefaultAssignmentRenderer } from './DefaultAssignmentRenderer.component'
import { FloorPlanDetailsRenderer } from './FloorPlanDetailsRenderer.component'
import { LivingAreaCalcDetailsRenderer } from './LivingAreaCalcDetailsRenderer.component'
import { MeasurementOnSiteDetailsRenderer } from './MeasurementOnSiteDetailsRenderer.component'
import { PhotoEditingDetailsRenderer } from './PhotoEditingDetailsRenderer.component'
import { PhotoVideoDetailsRenderer } from './PhotoVideoDetailsRenderer.component'
import { VirtualStagingDetailsRenderer } from './VirtualStagingDetailsRenderer.component'

/** Maps ProductKind to the corresponding assignment detail renderer component. */
export const rendererMap: Partial<Record<ProductKind, FC<AssignmentRendererProps>>> = {
  [ProductKind.ENERGY_CERTIFICATE_OF_NEED]: CertificateOfNeedsDetailsRenderer,
  [ProductKind.CONSUMPTION_CERTIFICATE]: ConsumptionCertificateDetailsRenderer,
  [ProductKind.FLOOR_PLAN_EDITING_2D]: FloorPlanDetailsRenderer,
  [ProductKind.FLOOR_PLAN_EDITING_3D]: FloorPlanDetailsRenderer,
  [ProductKind.MOBILE_APPLICATION_FLOOR_PLAN]: FloorPlanDetailsRenderer,
  [ProductKind.FLOOR_PLAN_CERTIFICATION]: LivingAreaCalcDetailsRenderer,
  [ProductKind.AUTHORITIES_DOCUMENTS]: AuthoritiesDocumentsDetailsRenderer,
  [ProductKind.MEASUREMENT_ON_SITE]: MeasurementOnSiteDetailsRenderer,
  [ProductKind.GROUND_PHOTO]: PhotoVideoDetailsRenderer,
  [ProductKind.DRONE_PHOTO]: PhotoVideoDetailsRenderer,
  [ProductKind.PHOTO]: PhotoVideoDetailsRenderer,
  [ProductKind.STANDARD_GROUND_VIDEO]: PhotoVideoDetailsRenderer,
  [ProductKind.STANDARD_DRONE_VIDEO]: PhotoVideoDetailsRenderer,
  [ProductKind.STANDARD_GROUND_AND_DRONE_VIDEO]: PhotoVideoDetailsRenderer,
  [ProductKind.GROUND_PHOTO_PRESTIGE]: PhotoVideoDetailsRenderer,
  [ProductKind.PRESTIGE_GROUND_AND_DRONE_VIDEO]: PhotoVideoDetailsRenderer,
  [ProductKind.MAST_PHOTO]: PhotoVideoDetailsRenderer,
  [ProductKind.REEL_VIDEO]: PhotoVideoDetailsRenderer,
  [ProductKind.TEASER_VIDEO]: PhotoVideoDetailsRenderer,
  [ProductKind.EXTERIOR_SHOOT]: PhotoVideoDetailsRenderer,
  [ProductKind.MATTERPORT_FLOOR_PLAN_WITH_VISIT]: FloorPlanDetailsRenderer,
  [ProductKind.MATTERPORT_FLOOR_PLAN_WITHOUT_VISIT]: FloorPlanDetailsRenderer,
  [ProductKind.HD]: PhotoVideoDetailsRenderer,
  [ProductKind.SD]: PhotoVideoDetailsRenderer,
  [ProductKind.FLOORFY_VIRTUAL_TOUR]: PhotoVideoDetailsRenderer,
}

/** The product fields the renderer dispatch reads (ISSUE-2745: flags and kind only). */
interface RendererProduct {
  category: ProductCategory
  kind: ProductKind
  isAiPhotoEditing?: boolean
}

/**
 * Resolves the renderer component for a product, falling back to DefaultAssignmentRenderer.
 * AI editing dispatches on the backend flag (AI-editing products can carry kind=PHOTO); the
 * certificate renderers dispatch on their dedicated kinds (ISSUE-2745).
 */
export function getAssignmentRenderer({ category, kind, isAiPhotoEditing }: RendererProduct): FC<AssignmentRendererProps> {
  const isStandardStaging = STAGING_PRODUCT_KINDS.has(kind)

  const renderer =
    (isAiPhotoEditing ? AIEditingDetailsRenderer : undefined)
    ?? (category === EditingCategory.EDITING && kind === ProductKind.PHOTO ? PhotoEditingDetailsRenderer : undefined)
    ?? (isStandardStaging ? VirtualStagingDetailsRenderer : undefined)
    ?? rendererMap[kind]

  if (!renderer) {
    logger.warn('No specialized renderer for ProductKind, using default', undefined, {
      context: {
        component: 'rendererRegistry', metadata: { category, kind, isAiPhotoEditing }
      },
    })
  }

  return renderer ?? DefaultAssignmentRenderer
}
